/**
 * WHYSHIFT Direct from the House — bar chart pricing on variant change.
 */
class WhyshiftHouseModelCard extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    this.#abort = new AbortController();
    const section = this.closest('.shopify-section');
    if (!section) return;

    section.addEventListener('variant:update', this.#onVariantUpdate, {
      signal: this.#abort.signal,
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
  }

  /** @param {CustomEvent} event */
  #onVariantUpdate = (event) => {
    const productId = this.dataset.productId;
    if (!productId || event.detail?.data?.productId !== productId) return;

    const variant = event.detail?.resource;
    if (!variant || typeof variant.price !== 'number') return;

    const lowerMult = Number(this.dataset.lowerMult) || 2;
    const upperMult = Number(this.dataset.upperMult) || 3;
    const editionNumber = this.dataset.editionNumber || '1';
    const editionTotal = this.dataset.editionTotal || '100';
    const editionSuffix = this.dataset.editionSuffix || 'editions';

    const priceDollars = Math.floor(variant.price / 100);
    const lowerRounded = this.#roundToNearestTen(priceDollars * lowerMult);
    const upperRounded = this.#roundToNearestTen(priceDollars * upperMult);
    const dropCents = this.#roundToWholeDollarCents(variant.price);

    const dropEl = this.querySelector('[data-house-drop-price]');
    const retailEl = this.querySelector('[data-house-retail-price]');
    const editionEl = this.querySelector('[data-house-edition]');

    if (dropEl) {
      dropEl.textContent = this.#formatWholeDollar(dropCents);
    }

    if (retailEl) {
      retailEl.innerHTML = `${this.#formatWholeDollar(lowerRounded * 100)}<span class="whyshift-house-model__range-sep" aria-hidden="true">-</span>${this.#formatWholeDollar(upperRounded * 100)}`;
    }

    if (editionEl) {
      editionEl.textContent = `${editionNumber} / ${editionTotal} ${editionSuffix}`;
    }

    this.#updateBarHeights(priceDollars, upperRounded);
  };

  /** @param {number} dropDollars @param {number} retailUpperDollars */
  #updateBarHeights(dropDollars, retailUpperDollars) {
    const chartMax = Math.max(retailUpperDollars, dropDollars, 1);
    let dropPct = Math.round((dropDollars / chartMax) * 100);
    let retailPct = Math.round((retailUpperDollars / chartMax) * 100);

    if (dropPct < 35) {
      dropPct = 35;
    }

    if (retailPct < dropPct) {
      retailPct = dropPct;
    }

    this.style.setProperty('--house-drop-bar-pct', String(dropPct));
    this.style.setProperty('--house-retail-bar-pct', String(retailPct));
  }

  /** @param {number} value */
  #roundToNearestTen(value) {
    return Math.round(value / 10) * 10;
  }

  /** @param {number} cents */
  #roundToWholeDollarCents(cents) {
    return Math.round(cents / 100) * 100;
  }

  /** @param {number} cents */
  #formatWholeDollar(cents) {
    const wholeCents = this.#roundToWholeDollarCents(cents);

    if (window.Shopify?.formatMoney) {
      const formatted = window.Shopify.formatMoney(wholeCents, window.theme?.moneyFormat);
      return formatted.replace(/[.,]00(?!\d)/, '');
    }

    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
      maximumFractionDigits: 0,
    }).format(wholeCents / 100);
  }
}

if (!customElements.get('whyshift-house-model-card')) {
  customElements.define('whyshift-house-model-card', WhyshiftHouseModelCard);
}
